import { useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { safeLocalStorageGet, safeLocalStorageRemove } from "@/lib/safe-browser";
import { clearPostLoginRedirect, replaceWithPostLoginRedirect } from "@/lib/auth-redirect";

let authUrlSessionPromise: Promise<Session | null> | null = null;

type AuthSnapshot = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
};

const serverSnapshot: AuthSnapshot = { user: null, session: null, isLoading: true };

function readPersistedSessionSync(): Session | null {
  if (typeof window === "undefined") return null;
  try {
    const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const storageKeys: string[] = [];
    if (url) {
      const ref = new URL(url).hostname.split(".")[0];
      storageKeys.push(`sb-${ref}-auth-token`);
    }
    // Mobile/PWA restores can run before Vite env substitution is readable in
    // this module. Fall back to the real Supabase auth storage key instead of
    // showing a signed-out/header-loading UI and then swapping it a second later.
    try {
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (key?.startsWith("sb-") && key.endsWith("-auth-token") && !storageKeys.includes(key)) {
          storageKeys.push(key);
        }
      }
    } catch {
      // restricted storage — just use the explicit key above if available
    }
    const raw = storageKeys.map((key) => safeLocalStorageGet(key)).find(Boolean);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const session: Session | null = parsed?.currentSession ?? parsed ?? null;
    if (!session?.access_token && !session?.refresh_token) return null;
    // Use the persisted user immediately for UI stability, even if the access
    // token may need refreshing. The async auth flow below still validates and
    // clears the session if refresh fails, but protected pages no longer flash
    // the signed-out UI during mobile reloads/navigation.
    return session;
  } catch {
    return null;
  }
}

const initialPersisted = readPersistedSessionSync();
// When there is no persisted session yet, keep `isLoading` true on the client
// until the async session check resolves. Otherwise, protected pages (e.g.
// /my-routes, /account) would instantly redirect to /login on a hard refresh
// before Supabase has had a chance to hydrate the session from storage/cookies.
let authSnapshot: AuthSnapshot = initialPersisted
  ? { session: initialPersisted, user: initialPersisted.user ?? null, isLoading: false }
  : { user: null, session: null, isLoading: typeof window !== "undefined" };
let authStarted = false;
let authSubscription: { unsubscribe: () => void } | null = null;
const authSubscribers = new Set<() => void>();

function emitAuthSnapshot(next: Partial<AuthSnapshot>) {
  authSnapshot = { ...authSnapshot, ...next };
  authSubscribers.forEach((notify) => notify());
}

function hasAuthParamsInUrl() {
  if (typeof window === "undefined") return false;
  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  return Boolean(
    hashParams.get("access_token") ||
    hashParams.get("refresh_token") ||
    hashParams.get("code") ||
    url.searchParams.get("access_token") ||
    url.searchParams.get("refresh_token") ||
    url.searchParams.get("code"),
  );
}

function isAuthCallbackPath() {
  return typeof window !== "undefined" && window.location.pathname === "/auth/callback";
}

function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(fallback), ms);
    promise
      .then((value) => resolve(value))
      .catch(() => resolve(fallback))
      .finally(() => window.clearTimeout(timer));
  });
}

async function authCallWithTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("Tempo esgotado ao validar o login")), ms);
    promise
      .then(resolve)
      .catch(reject)
      .finally(() => window.clearTimeout(timer));
  });
}

function cleanAuthParamsFromUrl() {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  const authKeys = [
    "access_token",
    "refresh_token",
    "expires_in",
    "expires_at",
    "token_type",
    "type",
    "provider_token",
    "provider_refresh_token",
    "code",
    "error",
    "error_code",
    "error_description",
  ];

  authKeys.forEach((key) => {
    url.searchParams.delete(key);
    hashParams.delete(key);
  });

  const nextHash = hashParams.toString();
  const nextUrl = `${url.pathname}${url.search}${nextHash ? `#${nextHash}` : ""}`;
  window.history.replaceState(window.history.state, document.title, nextUrl);
}

function consumeAuthSessionFromUrl() {
  // The dedicated callback route handles OAuth codes/tokens itself. If this
  // global auth store also tries to exchange the same code, the login can race
  // and one request comes back as invalid/empty, leaving the UI signed out.
  if (isAuthCallbackPath()) return Promise.resolve(null);
  if (!hasAuthParamsInUrl()) return Promise.resolve(null);
  if (authUrlSessionPromise) return authUrlSessionPromise;

  authUrlSessionPromise = (async () => {
    if (typeof window === "undefined") return null;

    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
    const getParam = (key: string) => hashParams.get(key) ?? url.searchParams.get(key);
    const accessToken = getParam("access_token");
    const refreshToken = getParam("refresh_token");
    const code = getParam("code");
    const authError = getParam("error_description") ?? getParam("error");

    if (authError) {
      cleanAuthParamsFromUrl();
      throw new Error(authError);
    }

    if (accessToken && refreshToken) {
      const { data, error } = await authCallWithTimeout(
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        }),
        30000,
      );
      if (error) throw error;
      cleanAuthParamsFromUrl();
      return data.session ?? null;
    }

    if (code) {
      const { data, error } = await authCallWithTimeout(supabase.auth.exchangeCodeForSession(code), 30000);
      if (error) throw error;
      cleanAuthParamsFromUrl();
      return data.session ?? null;
    }

    return null;
  })().catch((error) => {
    authUrlSessionPromise = null;
    throw error;
  });

  return authUrlSessionPromise;
}

function startAuthOnce() {
  if (authStarted || typeof window === "undefined") return;
  authStarted = true;

  // One shared listener for the whole app. Previously every useAuth() call had
  // its own session check, so the header could fall back to “Entrar” while
  // another part of the page had already received a valid session.
  const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
    if (newSession) {
      emitAuthSnapshot({ session: newSession, user: newSession.user ?? null, isLoading: false });
      // Auto-redirect away from the login screen the moment a session lands,
      // so the "Entrar" button never gets a chance to re-render.
      if (
        (event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") &&
        typeof window !== "undefined"
      ) {
        const path = window.location.pathname;
        if (path === "/login" || path === "/auth") {
          replaceWithPostLoginRedirect("/");
        }
      }
      return;
    }
    if (event === "SIGNED_OUT" || (event === "INITIAL_SESSION" && !hasAuthParamsInUrl())) {
      emitAuthSnapshot({ session: null, user: null, isLoading: false });
    }
  });
  authSubscription = listener.subscription;

  const safety = window.setTimeout(() => {
    if (authSnapshot.isLoading) emitAuthSnapshot({ isLoading: false });
  }, hasAuthParamsInUrl() ? 30000 : 5000);
  const hadAuthParamsAtStart = hasAuthParamsInUrl();

  (async () => {
    const urlSession = await consumeAuthSessionFromUrl();
    if (urlSession) return urlSession;

    const currentSession = await withTimeout(
      supabase.auth.getSession().then(({ data }) => data.session ?? null),
      4000,
      null,
    );
    return currentSession;
  })()
    .then((currentSession) => {
      window.clearTimeout(safety);
      if (currentSession) {
        emitAuthSnapshot({ session: currentSession, user: currentSession.user ?? null, isLoading: false });

        if (hadAuthParamsAtStart) {
          replaceWithPostLoginRedirect("/");
          return;
        }

        // Revalidate in the background; don't block the UI on this request.
        supabase.auth.getUser()
          .then(({ data }) => {
            if (data.user) emitAuthSnapshot({ user: data.user, session: authSnapshot.session });
          })
          .catch(() => undefined);
        return;
      }
      // Don't wipe a session that the auth listener may have already set
      // (race between getSession() and INITIAL_SESSION/SIGNED_IN events).
      if (authSnapshot.session) {
        emitAuthSnapshot({ isLoading: false });
        return;
      }
      emitAuthSnapshot({ session: null, user: null, isLoading: false });
    })
    .catch((err) => {
      window.clearTimeout(safety);
      console.warn("[useAuth] session check failed", err);
      if (authSnapshot.session) {
        emitAuthSnapshot({ isLoading: false });
        return;
      }
      emitAuthSnapshot({ session: null, user: null, isLoading: false });
    });
}

function subscribeAuth(notify: () => void) {
  startAuthOnce();
  authSubscribers.add(notify);
  // Keep the auth listener alive for the lifetime of the tab. Tearing it down
  // on transient unmounts (route transitions) caused getSession() to re-run and
  // could clobber a valid session with null on slow networks.
  return () => {
    authSubscribers.delete(notify);
  };
}

function getClientAuthSnapshot() {
  if (!authSnapshot.session && authSnapshot.isLoading) {
    const persisted = readPersistedSessionSync();
    if (persisted) {
      authSnapshot = {
        session: persisted,
        user: persisted.user ?? null,
        isLoading: false,
      };
    }
  }
  return authSnapshot;
}

export function useAuth() {
  const { user, session, isLoading } = useSyncExternalStore(
    subscribeAuth,
    getClientAuthSnapshot,
    () => serverSnapshot,
  );

  const signOut = async () => {
    // Optimistic local sign-out: clear state and persisted session immediately
    // so the UI reflects the change even on slow / offline networks.
    emitAuthSnapshot({ user: null, session: null, isLoading: false });
    try {
      if (typeof window !== "undefined") {
        const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
        if (url) {
          const ref = new URL(url).hostname.split(".")[0];
          safeLocalStorageRemove(`sb-${ref}-auth-token`);
        }
      }
    } catch { /* ignore */ }
    // Fire-and-forget the remote sign-out — don't block the UI on it.
    void supabase.auth.signOut().catch(() => undefined);
    if (typeof window !== "undefined") {
      clearPostLoginRedirect();
      window.location.replace("/login");
    }
  };

  return { user, session, isLoading, isAuthenticated: !!(user || session?.user), signOut };
}
